import { Capacitor, registerPlugin } from '@capacitor/core';
import { SafeArea, SafeAreaPlugin, type SafeAreaInsets } from 'capacitor-plugin-safe-area';
import { ref, onMounted, onUnmounted } from 'vue';
registerPlugin<SafeAreaPlugin>("SafeAreaPlugin");

type SafeAreaInsetsExcept = "top" | "bottom" | "left" | "right";

const setInsets = (insets: SafeAreaInsets["insets"], except: SafeAreaInsetsExcept[] = []) => {
    for (const [key, value] of Object.entries(insets)) {
        if (!except.includes(key as SafeAreaInsetsExcept)) {
            document.documentElement.style.setProperty(`--ion-safe-area-${key}`, `${value}px`);
        }
    }
};

export const setSafeAreaInsets = (except: SafeAreaInsetsExcept[] = []) => {
    SafeArea.getSafeAreaInsets().then(({ insets }) => {
        setInsets(insets, except);
    });
};

export const attachSafeAreaInsetsListener = (except: SafeAreaInsetsExcept[] = []) => {
    SafeArea.addListener('safeAreaChanged', ({ insets }) => {
        setInsets(insets, except);
    });
};

export const removeSafeAreaListeners = () => SafeArea.removeAllListeners();

export const attachEventListenersOnlyForAndroid = async () => {
    if (Capacitor.getPlatform() === 'android') {
        const except = ["bottom"] as SafeAreaInsetsExcept[];
        attachSafeAreaInsetsListener(except);
        return setSafeAreaInsets(except); //with non-immersive navigation bar, the bottom inset should be 0
    }
    return Promise.resolve();
};

/**
 * Vue composable for safe area insets
 * @returns Object with safeAreaInsets reactive reference
 */
export const useSafeArea = () => {
    const safeAreaInsets = ref({
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
    });

    onMounted(() => {
        // Get initial safe area insets
        SafeArea.getSafeAreaInsets().then(({ insets }: SafeAreaInsets) => {
            safeAreaInsets.value = insets;
        });

        // Listen for safe area changes
        SafeArea.addListener('safeAreaChanged', ({ insets }: SafeAreaInsets) => {
            safeAreaInsets.value = insets;
        });
    });

    onUnmounted(() => {
        SafeArea.removeAllListeners();
    });

    return {
        safeAreaInsets
    };
};